import React, { useMemo } from "react";
import { each, filter, isNull, map, size } from "lodash";
import styled from "styled-components";
import Map from "@charty/map";
import { isColor, transparentize } from "@glossy";
import { getFacetName } from "@linkfluence/i18n";
import { getConfigField } from "@linkfluence/konf";
import { Country } from "@linkfluence/models/country";
import { Facet } from "@linkfluence/models/facet";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { getCountryPopulation } from "@linkfluence/models/population";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { Clause } from "@radarly/models/clause";
import { isTermClause } from "@radarly/models/clause/is";
import { DatavizMap } from "@radarly/models/dataviz";
import { SearchFragmentGeo, getTermFromAggs } from "@radarly/models/search";
import Empty from "../../empty";
import Tooltip from "../../tooltip/metric";

const Container = styled.div`
  height: 100%;
`;
interface SharedWidgetsGeoMapInnerProps {
  data: SearchFragmentGeo["data"];
  fragment: SearchFragmentGeo;
  onClickItem?: (uid: string | number) => void;
  className?: string;
  dataviz: DatavizMap;
}

function formatGeoData(
  places: SearchFragmentGeo["data"]["aggs"][Facet.Towns]["values"],
  metric: Metric,
  locale: Locale
) {
  const cleanPlaces = filter(places, ({ key }) => key !== "");
  return map(cleanPlaces, ({ counts, location, key, isoCode }) => {
    if (location) {
      return {
        id: key,
        count: counts[metric],
        isoCode,
        label: location.name,
        lat: location.lat,
        lng: location.lng,
        population: location.population,
      };
    } else {
      return {
        id: key,
        count: counts[metric],
        population: getCountryPopulation(key.toUpperCase() as Country),
        label: getFacetName(locale, Facet.Countries, key),
      };
    }
  });
}

function getCountryFromClause(clause: Clause) {
  let country = null;
  each(clause.and, (innerClause) => {
    if (isTermClause(innerClause) && innerClause[Facet.Countries]) {
      const countries = innerClause[Facet.Countries];
      if (size(countries) === 1) {
        country = countries[0];
      }
    }
  });
  return country;
}

export default function SharedWidgetsGeoMapInner({
  data,
  fragment,
  onClickItem,
  dataviz,
  className,
}: SharedWidgetsGeoMapInnerProps) {
  const locale = useCurrentUserLocale();
  const field = getTermFromAggs(fragment.request.aggs);
  const metric = fragment.request.aggs[field].metrics[0];
  const geoData = useMemo(() => {
    return formatGeoData(
      data && data.aggs && data.aggs[field] ? data.aggs[field].values : [],
      metric,
      locale
    );
  }, [data, field, metric, locale]);
  const country = getCountryFromClause(fragment.request.query);
  const accessToken = getConfigField(["mapbox", "accessToken"], "");
  const defaultStyle = getConfigField(["mapbox", "styles", "default"], "");
  const geoJSONEndpoint = getConfigField(
    ["mapbox", "geo_json", "endpoint"],
    ""
  );
  const geoJSONWorldUrl = getConfigField(["mapbox", "geo_json", "world"], "");
  const variant =
    !!dataviz.variant && dataviz.variant !== "custom" ? dataviz.variant : null;
  return (
    <Empty empty={!geoData.length}>
      <Container className={className}>
        <Map
          locale={locale}
          data={geoData}
          country={country}
          styleUrl={defaultStyle}
          accessToken={accessToken}
          geoJsonWorldUrl={geoJSONWorldUrl}
          geoJsonEndpoint={geoJSONEndpoint}
          populations={dataviz.populations}
          variant={variant}
          colors={
            isNull(variant) && dataviz.color && isColor(dataviz.color)
              ? [dataviz.color, transparentize(0.75, dataviz.color)]
              : null
          }
          onClick={onClickItem}
          mode={
            field === Facet.Countries
              ? "countries"
              : field === Facet.Towns
              ? "towns"
              : "regions"
          }
          tooltip={(geo) => (
            <Tooltip
              count={geo.count}
              label={geo.label}
              metric={metric}
              locale={locale}
            />
          )}
        />
      </Container>
    </Empty>
  );
}
